import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  canUse,
  hasModule,
  type Entitlements,
  type FeatureKey,
  type MembershipRole,
  type ModuleCode,
  type Permission,
  type SessionResponse,
  type SessionUser,
  type StaffSession,
} from '@azko/shared';
import { ApiRequestError, api, setActiveCondominium } from './api.js';

interface AdminSessionResponse {
  user: SessionUser;
  staff: StaffSession;
}

/**
 * Quem esta logado pode ser uma de duas coisas, e elas nao se misturam:
 * alguem dentro de um condominio, ou alguem da equipe Azko — que nao pertence
 * a condominio nenhum e por isso nem aparece em `/auth/me`.
 */
type Boot =
  | { kind: 'tenant'; session: SessionResponse }
  | { kind: 'staff'; user: SessionUser; staff: StaffSession };

/**
 * O boot tenta o app do condominio primeiro, porque e o caso de quase toda
 * request. Falhou por falta de tenant (e nao por falta de login)? Entao pode
 * ser staff da Azko: `/admin/session` responde por quem vive fora do contexto
 * de condominio. Sem esta segunda tentativa, um `azko_admin` logava com sucesso
 * e caia de volta na tela de login, porque `/auth/me` nega quem nao tem
 * membership.
 */
async function loadBoot(): Promise<Boot> {
  try {
    return { kind: 'tenant', session: await api.get<SessionResponse>('/auth/me') };
  } catch (error) {
    // 401 = nao esta logado. Nao adianta perguntar ao backoffice.
    if (error instanceof ApiRequestError && error.status === 401) throw error;

    // Conta suspensa pela Azko: o vinculo existe e a senha estava certa, entao
    // perguntar ao backoffice so troca este erro por um 403 generico — e o
    // usuario voltaria para a tela de login achando que errou a senha. Sobe o
    // erro com o codigo intacto para o App mostrar o que realmente aconteceu.
    if (error instanceof ApiRequestError && error.code === 'account_suspended')
      throw error;

    const admin = await api.get<AdminSessionResponse>('/admin/session');
    return { kind: 'staff', user: admin.user, staff: admin.staff };
  }
}

/**
 * Sessao + entitlements em um contexto so.
 *
 * O front NAO decide permissao por nome de plano. Ele pergunta `can()` e
 * `hasModule()` — as mesmas funcoes que a API usa, vindas de @azko/shared.
 * Assim menu escondido e rota bloqueada nunca discordam do backend.
 *
 * Isto e experiencia de uso, nao seguranca: a API valida de novo em toda
 * request. Esconder o botao evita frustracao; quem burlar o front esbarra
 * no 402 do servidor.
 */
interface SessionContextValue {
  session: SessionResponse | null;
  entitlements: Entitlements | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Preenchido quando a Azko cortou o acesso do condominio deste usuario. */
  suspendedMessage: string | null;
  role: MembershipRole | null;
  /** Preenchido so para a equipe Azko; `null` para todo cliente. */
  staff: StaffSession | null;
  /** Usuario logado, venha ele do app do condominio ou do backoffice. */
  user: SessionUser | null;
  can: (feature: FeatureKey) => boolean;
  hasModuleAccess: (module: ModuleCode) => boolean;
  /** Permissao de RBAC — a mesma lista que o `authorize()` usa no backend. */
  allows: (permission: Permission) => boolean;
  switchCondominium: (publicId: string) => void;
  refresh: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['session'],
    queryFn: loadBoot,
    // 401 e resposta esperada de quem nao esta logado — nao adianta repetir.
    retry: (failureCount, error) =>
      error instanceof ApiRequestError && error.status === 401 ? false : failureCount < 1,
    staleTime: 5 * 60_000,
  });

  const value = useMemo<SessionContextValue>(() => {
    const boot = data ?? null;
    const session = boot?.kind === 'tenant' ? boot.session : null;
    const staff = boot?.kind === 'staff' ? boot.staff : null;
    const entitlements = session?.entitlements ?? null;
    const permissions = new Set<Permission>(session?.permissions ?? staff?.permissions ?? []);

    return {
      session,
      entitlements,
      isLoading,
      isAuthenticated: Boolean(boot),
      suspendedMessage:
        error instanceof ApiRequestError && error.code === 'account_suspended'
          ? error.message
          : null,
      role: session?.active?.role ?? null,
      staff,
      user: boot?.kind === 'staff' ? boot.user : (session?.user ?? null),
      can: (feature) => (entitlements ? canUse(entitlements, feature) : false),
      hasModuleAccess: (module) => (entitlements ? hasModule(entitlements, module) : false),
      allows: (permission) => permissions.has(permission),
      switchCondominium: (publicId) => {
        setActiveCondominium(publicId);
        // Troca de condominio invalida TODO cache: os dados em tela pertencem
        // ao tenant anterior.
        void queryClient.invalidateQueries();
      },
      refresh: () => void queryClient.invalidateQueries({ queryKey: ['session'] }),
    };
  }, [data, error, isLoading, queryClient]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession precisa estar dentro de <SessionProvider>');
  return context;
}
